import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PUZZLES, MAX_WRONG_ATTEMPTS } from '../constants';

// ── 工具函式 ────────────────────────────────────────────────────────────────
function shuffleArray(arr) {
  const a = arr.map((char, originalIdx) => ({ char, originalIdx }));
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ── 常數 ─────────────────────────────────────────────────────────────────────
const COLS = 10; // 每列幾個字

// ── 主頁面 ──────────────────────────────────────────────────────────────────
export default function WordPuzzlePage() {
  const navigate = useNavigate();

  // 題目索引
  const [puzzleIdx, setPuzzleIdx] = useState(0);

  // 遊戲階段：'playing' | 'success' | 'failed'
  const [phase, setPhase] = useState('playing');

  // 錯誤次數
  const [wrongCount, setWrongCount] = useState(0);

  // 打亂後的文字池（每格含 { char, originalIdx, id }）
  const [pool, setPool] = useState([]);

  // 已選入答案欄的格子：Map<answerPos, poolId>
  const [selected, setSelected] = useState(new Map());

  // 錯誤動畫觸發
  const [shakeAnswer, setShakeAnswer] = useState(false);

  // 已提示的答案位置 Set<number>（位置 index，不是 pool id）
  const [hintPositions, setHintPositions] = useState(new Set());

  const puzzle = PUZZLES[puzzleIdx];
  const answerLen = puzzle.answer.length;

  // ── 初始化 / 換題 ──────────────────────────────────────────────────────────
  useEffect(() => {
    const shuffled = shuffleArray(puzzle.chars).map((item, idx) => ({
      ...item,
      id: idx,
    }));
    setPool(shuffled);
    setSelected(new Map());
    setWrongCount(0);
    setHintPositions(new Set());
    setPhase('playing');
    setShakeAnswer(false);
  }, [puzzleIdx, puzzle.chars]);

  // ── 已用掉的 pool id 集合（僅玩家選的）──────────────────────────────────
  const usedIds = useMemo(() => new Set(selected.values()), [selected]);

  // ── 需要玩家填的位置（排除已提示的），依序作為「下一格」──────────────────
  const openPositions = useMemo(
    () => Array.from({ length: answerLen }, (_, i) => i).filter((i) => !hintPositions.has(i)),
    [answerLen, hintPositions],
  );

  // ── 點選文字池：填入下一個空格 ───────────────────────────────────────────
  const handlePickChar = (item) => {
    if (phase !== 'playing') return;
    if (usedIds.has(item.id)) return;

    // 找第一個尚未填的 open 位置
    const nextPos = openPositions.find((pos) => !selected.has(pos));
    if (nextPos === undefined) return;

    const next = new Map(selected);
    next.set(nextPos, item.id);
    setSelected(next);

    // 所有 open 位置都填滿了就判斷
    if (openPositions.every((pos) => next.has(pos))) {
      checkAnswer(next);
    }
  };

  // ── 點選答案格：取消該格，後面的字不動 ──────────────────────────────────
  const handleRemoveSlot = (pos) => {
    if (phase !== 'playing') return;
    if (hintPositions.has(pos)) return; // 提示格不能取消
    if (!selected.has(pos)) return;
    const next = new Map(selected);
    next.delete(pos);
    setSelected(next);
  };

  // ── 完整答案陣列（提示字 + 玩家選擇）────────────────────────────────────
  const answerSlots = useMemo(() => {
    return Array.from({ length: answerLen }, (_, i) => {
      if (hintPositions.has(i)) {
        return { char: puzzle.answer[i], isHint: true };
      }
      const id = selected.get(i) ?? null;
      return {
        char: id !== null ? (pool.find((p) => p.id === id)?.char ?? '') : '',
        isHint: false,
      };
    });
  }, [hintPositions, selected, pool, puzzle.answer, answerLen]);

  // ── 答錯後決定要提示哪個位置 ─────────────────────────────────────────────
  const pickHintPosition = (selMap, currentHintPositions) => {
    const currentOpen = Array.from({ length: answerLen }, (_, i) => i).filter(
      (i) => !currentHintPositions.has(i),
    );
    // 找出玩家這次填對位置的格子
    const correctPositions = currentOpen.filter((pos) => {
      const char = pool.find((p) => p.id === selMap.get(pos))?.char ?? '';
      return char === puzzle.answer[pos];
    });

    if (correctPositions.length > 0) {
      return correctPositions[Math.floor(Math.random() * correctPositions.length)];
    }
    // 全錯：從尚未提示的位置隨機選一個
    const remaining = currentOpen.filter((pos) => !currentHintPositions.has(pos));
    return remaining[Math.floor(Math.random() * remaining.length)] ?? null;
  };

  // ── 判斷 ──────────────────────────────────────────────────────────────────
  const checkAnswer = (selMap) => {
    const isCorrect = openPositions.every((pos) => {
      const char = pool.find((p) => p.id === selMap.get(pos))?.char ?? '';
      return char === puzzle.answer[pos];
    });

    if (isCorrect) {
      setPhase('success');
    } else {
      const nextWrong = wrongCount + 1;
      setWrongCount(nextWrong);
      setShakeAnswer(true);
      setTimeout(() => {
        setShakeAnswer(false);
        setSelected(new Map());
        if (nextWrong >= MAX_WRONG_ATTEMPTS) {
          setPhase('failed');
        } else {
          const newPos = pickHintPosition(selMap, hintPositions);
          if (newPos !== null) {
            setHintPositions((prev) => new Set([...prev, newPos]));
          }
        }
      }, 600);
    }
  };

  // ── 下一題 ────────────────────────────────────────────────────────────────
  const handleNext = () => {
    if (puzzleIdx < PUZZLES.length - 1) {
      setPuzzleIdx((i) => i + 1);
    } else {
      // 全部完成
      setPhase('allDone');
    }
  };

  // ── 重試 ─────────────────────────────────────────────────────────────────
  const handleRetry = () => {
    const shuffled = shuffleArray(puzzle.chars).map((item, idx) => ({
      ...item,
      id: idx,
    }));
    setPool(shuffled);
    setSelected(new Map());
    setWrongCount(0);
    setPhase('playing');
    setShakeAnswer(false);
  };

  // ── 文字池：按列分組（每列 COLS 個）────────────────────────────────────────
  const rows = useMemo(() => {
    const result = [];
    for (let i = 0; i < pool.length; i += COLS) {
      result.push(pool.slice(i, i + COLS));
    }
    return result;
  }, [pool]);

  // ── 顏色 ─────────────────────────────────────────────────────────────────
  const gold = '#B39029';
  const goldLight = '#D4AF37';
  const primary = '#907654';
  const bg = '#FAF7F3';
  const textMain = '#1B1A1E';
  const textSub = '#6E625B';

  // ── 覆蓋畫面（成功/失敗/全部完成）────────────────────────────────────────
  const renderOverlay = () => {
    if (phase === 'playing') return null;

    let emoji, title, sub, btnLabel, onBtn;

    if (phase === 'success') {
      emoji = '🪔';
      title = '答對了！';
      sub = puzzle.answer.join('');
      btnLabel = puzzleIdx < PUZZLES.length - 1 ? '下一題' : '完成所有題目';
      onBtn = handleNext;
    } else if (phase === 'failed') {
      emoji = '🙏';
      title = '再想想看';
      sub = `正確答案：${puzzle.answer.join('')}`;
      btnLabel = puzzleIdx < PUZZLES.length - 1 ? '繼續下一題' : '完成';
      onBtn = handleNext;
    } else {
      // allDone
      emoji = '☀️';
      title = '全部完成！';
      sub = '功德圓滿';
      btnLabel = '返回';
      onBtn = () => navigate(-1);
    }

    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(250,247,243,0.92)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 24,
          zIndex: 100,
          backdropFilter: 'blur(4px)',
        }}
      >
        <div style={{ fontSize: 64, lineHeight: 1 }}>{emoji}</div>
        <div
          style={{
            fontSize: 32,
            fontWeight: 800,
            color: phase === 'failed' ? '#C0392B' : gold,
            letterSpacing: 2,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 22,
            color: textSub,
            letterSpacing: 4,
            padding: '12px 28px',
            background: 'white',
            borderRadius: 16,
            border: `1.5px solid ${goldLight}`,
            boxShadow: `0 2px 16px rgba(179,144,41,0.12)`,
          }}
        >
          {sub}
        </div>
        {/* 進度點 */}
        <div style={{ display: 'flex', gap: 8 }}>
          {PUZZLES.map((_, i) => (
            <div
              key={i}
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: i <= puzzleIdx ? goldLight : '#DDD',
              }}
            />
          ))}
        </div>
        <button
          onClick={onBtn}
          style={{
            marginTop: 8,
            padding: '14px 48px',
            borderRadius: 32,
            border: 'none',
            background: `linear-gradient(135deg, ${goldLight}, ${gold})`,
            color: 'white',
            fontSize: 20,
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: `0 4px 16px rgba(179,144,41,0.35)`,
            letterSpacing: 2,
          }}
        >
          {btnLabel}
        </button>
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        minHeight: '100dvh',
        background: bg,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'var(--active-font-family, sans-serif)',
        maxWidth: 480,
        margin: '0 auto',
        position: 'relative',
      }}
    >
      {/* ── Header ── */}
      <div
        style={{
          padding: '16px 20px 14px',
          background: 'white',
          borderBottom: `2px solid ${goldLight}`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          position: 'relative',
        }}
      >
        {/* X 關閉按鈕（右上角） */}
        <button
          onClick={() => navigate(-1)}
          aria-label="關閉"
          style={{
            position: 'absolute',
            top: 14,
            right: 16,
            width: 36,
            height: 36,
            borderRadius: '50%',
            border: `1.5px solid #CCC`,
            background: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
            color: textSub,
            lineHeight: 1,
          }}
        >
          ✕
        </button>

        {/* 標題 + 題號 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, paddingRight: 44 }}>
          <span
            style={{
              fontSize: 20,
              fontWeight: 800,
              color: textMain,
              letterSpacing: 2,
              flex: 1,
            }}
          >
            佛法填字
          </span>
          <span style={{ fontSize: 14, color: textSub }}>
            {puzzleIdx + 1} / {PUZZLES.length}
          </span>
        </div>

        {/* 提示 */}
        <div
          style={{
            fontSize: 15,
            color: textSub,
            background: '#F5F1E8',
            borderRadius: 10,
            padding: '8px 12px',
            lineHeight: 1.6,
          }}
        >
          {puzzle.hint}
        </div>

        {/* 錯誤次數指示 */}
        <div style={{ display: 'flex', gap: 6, marginTop: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: textSub }}>失誤：</span>
          {Array.from({ length: MAX_WRONG_ATTEMPTS }).map((_, i) => (
            <div
              key={i}
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: i < wrongCount ? '#C0392B' : '#DDD',
                transition: 'background 0.3s',
              }}
            />
          ))}
          <span style={{ fontSize: 12, color: '#C0392B', marginLeft: 4 }}>
            {wrongCount > 0 ? `× ${wrongCount}` : ''}
          </span>
        </div>
      </div>

      {/* ── 文字池 ── */}
      <div style={{ flex: 1, padding: '20px 12px 0' }}>
        <div
          style={{
            fontSize: 13,
            color: textSub,
            marginBottom: 10,
            textAlign: 'center',
            letterSpacing: 1,
          }}
        >
          點選文字填入答案
        </div>

        {rows.map((row, rIdx) => (
          <div
            key={rIdx}
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 6,
              marginBottom: 6,
            }}
          >
            {row.map((item) => {
              const used = usedIds.has(item.id);
              return (
                <button
                  key={item.id}
                  onClick={() => handlePickChar(item)}
                  disabled={used || phase !== 'playing'}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    border: used
                      ? `1.5px dashed #CCC`
                      : `1.5px solid ${goldLight}`,
                    background: used ? '#F0EAE4' : 'white',
                    color: used ? '#CCC' : textMain,
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: used || phase !== 'playing' ? 'default' : 'pointer',
                    transition: 'all 0.15s',
                    boxShadow: used ? 'none' : '0 1px 4px rgba(0,0,0,0.08)',
                    flexShrink: 0,
                  }}
                >
                  {used ? '' : item.char}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* ── 答案區 ── */}
      <div
        style={{
          padding: '24px 20px 40px',
          background: 'white',
          borderTop: `2px solid ${goldLight}`,
          boxShadow: '0 -2px 12px rgba(0,0,0,0.05)',
        }}
      >
        <div
          style={{
            fontSize: 13,
            color: textSub,
            marginBottom: 12,
            textAlign: 'center',
            letterSpacing: 1,
          }}
        >
          點選已填的字可取消
        </div>

        {/* 答案格 */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 8,
            flexWrap: 'wrap',
            animation: shakeAnswer ? 'puzzleShake 0.5s ease' : 'none',
          }}
        >
          {answerSlots.map((slot, i) => {
            const filled = !!slot.char;
            const canRemove = !slot.isHint && filled && phase === 'playing';
            return (
              <div
                key={i}
                onClick={() => handleRemoveSlot(i)}
                style={{
                  width: 44,
                  height: 52,
                  borderRadius: 10,
                  border: slot.isHint
                    ? `2px solid ${gold}`
                    : filled
                    ? `2px solid ${goldLight}`
                    : `2px dashed #CCC`,
                  background: slot.isHint
                    ? `linear-gradient(135deg, #FFF8DC, #FFE88A)`
                    : filled
                    ? `linear-gradient(135deg, #FFFDF5, #FFF8E7)`
                    : '#FAFAFA',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 22,
                  fontWeight: 700,
                  color: slot.isHint ? gold : textMain,
                  boxShadow: filled || slot.isHint
                    ? `0 2px 8px rgba(179,144,41,0.18)`
                    : 'none',
                  transition: 'all 0.15s',
                  cursor: canRemove ? 'pointer' : 'default',
                }}
              >
                {slot.char}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Overlay ── */}
      {renderOverlay()}

      {/* ── Shake keyframes（注入 style tag）── */}
      <style>{`
        @keyframes puzzleShake {
          0%   { transform: translateX(0); }
          15%  { transform: translateX(-8px); }
          35%  { transform: translateX(8px); }
          55%  { transform: translateX(-6px); }
          75%  { transform: translateX(6px); }
          90%  { transform: translateX(-3px); }
          100% { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
